'use client'

import { useEffect } from 'react'

/**
 * Prevents the View Transitions API from holding the old page snapshot visible
 * during native browser back/forward gesture navigation.
 *
 * next-view-transitions wraps every popstate in document.startViewTransition(),
 * which screenshots the current page and holds it on screen while React renders
 * the new page. For programmatic navigation (button clicks) this is fine — we
 * want our CSS slide animation. But for native OS gestures the old snapshot
 * causes a brief flash of the previous page (e.g. Spec flickering after going
 * back to PDP).
 *
 * Fix: override startViewTransition so that when no direction was pre-set
 * (native gesture), the callback runs immediately — no screenshot, no hold,
 * page updates instantly. When direction IS pre-set (programmatic), the real
 * browser API runs as normal and our CSS animations play.
 *
 * Cleanup: the data-nav-direction attribute is removed via transition.finished
 * rather than a fixed setTimeout. This prevents a race condition in production
 * where the navigation callback (fetching a JS chunk) can take longer than any
 * hardcoded timeout, deleting the attribute before the CSS animation starts.
 */
type StartViewTransitionFn = (
  callback?: (() => Promise<void> | void) | StartViewTransitionOptions,
) => ViewTransition

export function NavDirectionTracker() {
  useEffect(() => {
    if (!('startViewTransition' in document)) {
      return
    }

    const original = (
      document.startViewTransition as StartViewTransitionFn
    ).bind(document)

    ;(
      document as Document & { startViewTransition: StartViewTransitionFn }
    ).startViewTransition = function (callback) {
      if (document.documentElement.dataset.navDirection) {
        const transition = original(callback)

        transition.finished.finally(() => {
          delete document.documentElement.dataset.navDirection
        })

        return transition
      }

      // Native gesture: skip the snapshot/animation, run update immediately.
      const fn = typeof callback === 'function' ? callback : undefined
      const updateCallbackDone = Promise.resolve().then(() => fn?.())

      return {
        ready: updateCallbackDone,
        finished: updateCallbackDone,
        updateCallbackDone,
        skipTransition: () => {},
      } as unknown as ViewTransition
    }

    return () => {
      ;(
        document as Document & { startViewTransition: StartViewTransitionFn }
      ).startViewTransition = original
    }
  }, [])

  return null
}
